import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShanySidebar } from "./components/ShanySidebar";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import History from "./pages/History";
import Settings from "./pages/Settings";
import ShanyDashboard from "./pages/ShanyDashboard";
import EmailDashboard from "./pages/EmailDashboard";
import SentDocuments from "./pages/SentDocuments";
import AutomationsHub from "./pages/AutomationsHub";
import WABroadcast from "./pages/WABroadcast";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ClientDashboard from "./pages/ClientDashboard";
import ClientInvoicesPage from "./pages/ClientInvoicesPage";
import ClientCampaignsPage from "./pages/ClientCampaignsPage";
import FullFrameDashboard from "./pages/fullframe/FullFrameDashboard";
import FullFrameProduction from "./pages/fullframe/FullFrameProduction";
import PublicProductionView from "./pages/fullframe/PublicProductionView";
import FullFrameCompanyExpenses from "./pages/fullframe/FullFrameCompanyExpenses";
import NonnaDashboard from "./pages/nonna/NonnaDashboard";
import SendCampaignsPage from "./pages/SendCampaignsPage";
import FlowUpPage from "./FlowUpPage";
import { UndoProvider } from "./pages/fullframe/UndoStack";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./lib/auth";
import DevBypassBanner from "./components/DevBypassBanner";
import { api } from "./lib/api";

// Route /S.ADMIN/invoices → either WhatsApp Dashboard OR Email Dashboard based on company channels
function InvoicesRouter() {
  const location = useLocation();
  const [resolved, setResolved] = useState(null);
  const params = new URLSearchParams(location.search);
  const companyId = params.get("company");

  useEffect(() => {
    if (!companyId) { setResolved("whatsapp"); return; }
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const c = (data.companies || []).find((x) => x.id === companyId);
        const channels = c?.channels || [];
        if (channels.includes("email") && !channels.includes("whatsapp")) setResolved("email");
        else setResolved("whatsapp");
      } catch { setResolved("whatsapp"); }
    })();
  }, [companyId]);

  if (resolved === null) return <div className="p-8 text-center text-stone-500">טוען...</div>;
  return resolved === "email" ? <EmailDashboard /> : <Dashboard />;
}

/** Auto-pin a normal owner to their own company by injecting `?company=...`
 *  into the URL whenever they hit an admin page without one. Super-admins
 *  are exempt (they pick the company manually). */
function CompanyPinner({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasCompany = !!params.get("company");
  if (user && user.role === "owner" && user.company_id && !hasCompany) {
    params.set("company", user.company_id);
    return <Navigate to={`${location.pathname}?${params.toString()}`} replace />;
  }
  return children;
}

/**
 * Bespoke per-tenant dashboards. Each entry maps a slug to a custom React
 * page. When a slug is NOT in this registry, the standard `ClientDashboard`
 * (with Invoice + Campaigns subscription tiles) is rendered instead.
 */
const BESPOKE_CLIENTS = {
  "full-frame": {
    dashboard: FullFrameDashboard,
    production: FullFrameProduction,
    expenses: FullFrameCompanyExpenses,
  },
  "nonna-nonna": {
    dashboard: NonnaDashboard,
  },
};

/** Normalise a URL slug → lookup key for the BESPOKE_CLIENTS registry.
 *  Accepts any casing and ignores dashes/underscores so `/FullFrame`,
 *  `/full-frame`, `/Full_Frame`, `/fullframe` all map to the same entry. */
function lookupBespoke(slug) {
  const key = (slug || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  for (const [registered, entry] of Object.entries(BESPOKE_CLIENTS)) {
    if (registered.replace(/[^a-z0-9]/g, "") === key) return entry;
  }
  return null;
}

/** Routes /:slug to either a bespoke dashboard OR the standard one. */
function ClientDashboardRouter() {
  const { slug } = useParams();
  const entry = lookupBespoke(slug);
  if (entry?.dashboard) {
    const Custom = entry.dashboard;
    return <Custom />;
  }
  return <ClientDashboard />;
}

/** Routes /:slug/productions/:prodId to the bespoke production detail
 *  if the slug has one registered, otherwise 404 → redirect to dashboard. */
function ClientProductionRouter() {
  const { slug } = useParams();
  const entry = lookupBespoke(slug);
  if (entry?.production) {
    const Page = entry.production;
    return <Page />;
  }
  return <Navigate to={`/${slug}`} replace />;
}

/** Routes /:slug/expenses (company-wide expenses page) — bespoke only. */
function ClientExpensesRouter() {
  const { slug } = useParams();
  const entry = lookupBespoke(slug);
  if (entry?.expenses) {
    const Page = entry.expenses;
    return <Page />;
  }
  return <Navigate to={`/${slug}`} replace />;
}

function AppShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/S.ADMIN");
  const isLegacyAdmin = location.pathname.startsWith("/12345");
  const isFlowUp = location.pathname.startsWith("/flowup");
  const isAuthPage = ["/login", "/register", "/auth/callback", "/auth/forgot-password", "/auth/reset-password"].includes(location.pathname);

  // Legacy /12345 URL still bookmarked anywhere? Send the user to the
  // new /S.ADMIN equivalent and preserve any query string.
  if (isLegacyAdmin) {
    const newPath = location.pathname.replace(/^\/12345/, "/S.ADMIN");
    return <Navigate to={newPath + location.search} replace />;
  }

  if (isAuthPage) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      </Routes>
    );
  }

  // Owner spec 2026-07-08l — Public read-only share view. Must be
  // matched BEFORE the ProtectedRoute wrapper so anyone with the link
  // can view without logging in.
  if (window.location.pathname.startsWith("/share/production/")) {
    return (
      <Routes>
        <Route path="/share/production/:token" element={<PublicProductionView />} />
      </Routes>
    );
  }

  // FlowUp suite — its OWN routing tree (wouter-based). We short-circuit
  // here BEFORE the isAdmin / public branches so /flowup never falls into
  // <Home /> (whose dev-bypass redirects to /S.ADMIN) and never matches
  // /:slug. The wasender app handles all child routes internally.
  if (isFlowUp) {
    // No ProtectedRoute wrapper — the FlowUp suite is admin-only via the
    // ClientAdminSidebar entry point. Wrapping it in ProtectedRoute caused
    // an infinite ping-pong: /flowup → /login → dev-bypass → /S.ADMIN.
    return (
      <Routes>
        <Route path="/flowup/*" element={<FlowUpPage />} />
        <Route path="/flowup" element={<FlowUpPage />} />
      </Routes>
    );
  }

  if (isAdmin) {
    return (
      <ProtectedRoute>
        <Routes>
          {/* Direct full-page entry to FlowUpSender (no S.ADMIN wrapper).
              Listed FIRST so it wins specificity over the catch-all
              `/:slug` route below. */}
          <Route path="/send-campaigns" element={<SendCampaignsPage />} />
          {/* FlowUp (WaSender suite) — entire React+wouter app mounted
              under /flowup/*. Click the "פלו אפ" button in ClientAdminSidebar
              to enter. The wasender app has its own internal routing
              (dashboard, devices, contacts, campaigns, templates, etc.) */}
          <Route path="/flowup/*" element={<FlowUpPage />} />
          <Route path="/flowup" element={<FlowUpPage />} />
          {/* /S.ADMIN is the SUPER-ADMIN landing page — no left sidebar. The
              user requested a clean grid; they navigate INTO each company's
              dashboard from its cube. */}
          <Route path="/S.ADMIN" element={
            <main className="min-h-screen">
              <CompanyPinner><ShanyDashboard /></CompanyPinner>
            </main>
          } />
          {/* All other admin routes (per-service tooling) keep the legacy
              left sidebar for backwards compatibility. */}
          <Route path="/S.ADMIN/*" element={
            <>
              <ShanySidebar />
              <main className="lg:mr-64 min-h-screen">
                <Routes>
                  <Route path="invoices" element={<CompanyPinner><InvoicesRouter /></CompanyPinner>} />
                  <Route path="sent" element={<CompanyPinner><SentDocuments /></CompanyPinner>} />
                  <Route path="automations" element={<CompanyPinner><AutomationsHub /></CompanyPinner>} />
                  <Route path="automations/wa-broadcast" element={<CompanyPinner><WABroadcast /></CompanyPinner>} />
                  <Route path="tax-invoices" element={<Navigate to="/S.ADMIN/invoices?doc=tax_invoice" replace />} />
                  <Route path="history" element={<CompanyPinner><History /></CompanyPinner>} />
                  <Route path="settings" element={<CompanyPinner><Settings /></CompanyPinner>} />
                </Routes>
              </main>
            </>
          } />
          {/* Super admin can ALSO access each client's bespoke dashboard
              directly — same routes as the white-label customer view.
              When `isAdmin` is true, the dashboards themselves render an
              extra "Admin Tools" sidebar for service-level configuration. */}
          <Route path="/" element={<Home />} />
          <Route path="/:slug/invoices" element={<ClientInvoicesPage />} />
          <Route path="/:slug/campaigns" element={<ClientCampaignsPage />} />
          <Route path="/:slug/productions/:prodId" element={<ClientProductionRouter />} />
          <Route path="/:slug/expenses" element={<ClientExpensesRouter />} />
          <Route path="/:slug" element={<ClientDashboardRouter />} />
        </Routes>
      </ProtectedRoute>
    );
  }

  return (
    <main className="min-h-screen">
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/send-campaigns" element={<ProtectedRoute><SendCampaignsPage /></ProtectedRoute>} />
        <Route path="/services/whatsapp/invoices" element={<Navigate to="/S.ADMIN/invoices" replace />} />
        <Route path="/services/whatsapp/tax-invoices" element={<Navigate to="/S.ADMIN/tax-invoices" replace />} />
        <Route path="/history" element={<Navigate to="/S.ADMIN/history" replace />} />
        <Route path="/settings" element={<Navigate to="/S.ADMIN/settings" replace />} />
        {/* Customer's white-label dashboard at /{slug} — must be the LAST routes to avoid catching admin paths */}
        <Route path="/:slug/invoices" element={<ProtectedRoute><ClientInvoicesPage /></ProtectedRoute>} />
        <Route path="/:slug/campaigns" element={<ProtectedRoute><ClientCampaignsPage /></ProtectedRoute>} />
        <Route path="/:slug/productions/:prodId" element={<ProtectedRoute><ClientProductionRouter /></ProtectedRoute>} />
        <Route path="/:slug/expenses" element={<ProtectedRoute><ClientExpensesRouter /></ProtectedRoute>} />
        <Route path="/:slug" element={<ProtectedRoute><ClientDashboardRouter /></ProtectedRoute>} />
      </Routes>
    </main>
  );
}

function App() {
  return (
    <div className="App grain-bg" dir="rtl">
      <BrowserRouter>
        <AuthProvider>
          <DevBypassBanner />
          <UndoProvider>
            <AppShell />
          </UndoProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
